import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerView from "@/pages/CustomerView";
import PagingCustomerView from "@/pages/PagingCustomerView";
import QRRedirect from "@/pages/QRRedirect";
import Goodbye from "@/pages/Goodbye";
import HowItWorks from "@/pages/HowItWorks";
import ResetPassword from "@/pages/ResetPassword";

// Dismiss the static pre-React Olafel splash once any React content paints
function InitialLoaderDismisser() {
  useEffect(() => {
    const el = document.getElementById('initial-loader');
    if (!el) return;
    // Slight delay so the bouncing splash is visible at least briefly even on fast loads
    const t = setTimeout(() => {
      el.classList.add('hide');
      setTimeout(() => el.parentNode && el.parentNode.removeChild(el), 400);
    }, 250);
    return () => clearTimeout(t);
  }, []);
  return null;
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="w-8 h-8 border-3 border-orange-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Admin can access both /admin and /dashboard (if they also have a restaurant)
  if (requiredRole === 'host' && user.role === 'admin' && user.restaurant_id) {
    return children; // admin+host combo
  }
  // Staff users share the Host dashboard route — access to specific tabs is
  // gated server-side and via the Access Control toggles set by the host.
  if (requiredRole === 'host' && user.role === 'staff') {
    return children;
  }
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="w-8 h-8 border-3 border-orange-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <InitialLoaderDismisser />
      <Toaster position="bottom-right" richColors duration={2500} />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<ProtectedRoute requiredRole="host"><Dashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/r/:restaurantId/table/:tableId" element={<CustomerView />} />
          <Route path="/p/:restaurantId/:pagingId" element={<PagingCustomerView />} />
          <Route path="/q/:code" element={<QRRedirect />} />
          <Route path="/goodbye" element={<Goodbye />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
